import * as uiAnalyticsConsumer from '@servicenow/ui-analytics-consumer';
import {getInteractionId} from '@servicenow/ui-metrics';
import {createHttpErrorAction, resolveDomainContext} from '@servicenow/ui-effect-helpers';
import {sendRequest} from './http';
import {configConstants, getConfigItem} from '@servicenow/ui-config';

import {
	createSubscriptionId,
	createRegistryId,
	getAMBClient,
	handleGraphQLSubscription
} from './subscriptions';

import {createGraphQLDocument, getOperationName, getSpanOptions} from './utils';

import {
	defer,
	pick,
	addPendingNetworkActionForHost,
	isEqual,
	removePendingNetworkActionForHost
} from '@servicenow/ui-internal';

import documentToQueryString from './http/documentToQueryString';
import QueryContext from './queryContext';
import {SPAN_FOR_GRAPHQL, SPAN_KIND_CLIENT} from './constants';
const {trace, isValidTraceId} = uiAnalyticsConsumer;
const ambChannelRegistry = new Map();

export const GRAPHQL_REQUEST_STARTED = 'GRAPHQL_REQUEST_STARTED';
export const GRAPHQL_REQUEST_SUCCEEDED = 'GRAPHQL_REQUEST_SUCCEEDED';
export const GRAPHQL_REQUEST_FAILED = 'GRAPHQL_REQUEST_FAILED';
export const GRAPHQL_CACHE_UPDATED = 'GRAPHQL_CACHE_UPDATED';

const {GQL_CACHING_REFRESH_DELAY} = configConstants;

const cacheDelay = getConfigItem(GQL_CACHING_REFRESH_DELAY) || 10000;

export async function hashQuery(rawQuery) {
	const gqlDoc = createGraphQLDocument(rawQuery);
	const queryString = documentToQueryString(gqlDoc);
	const msgUint8 = new TextEncoder().encode(queryString);
	const hashBuffer = await crypto.subtle.digest('SHA-256', msgUint8);
	const hashArray = Array.from(new Uint8Array(hashBuffer));
	const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
	return hashHex;
}

export function* gqlEffect(
	query,
	options = {},
	subscriptionId,
	getAMBClient,
	ambChannelRegistry,
	coeffects
) {
	const {
		variableList = [],
		templateVarList = [],
		headerList = [],
		cacheable = false,
		__unstableQueryHash = null,
		startActionType = GRAPHQL_REQUEST_STARTED,
		streaming = null,
		successActionType = GRAPHQL_REQUEST_SUCCEEDED,
		errorActionType = GRAPHQL_REQUEST_FAILED,
		cacheUpdateActionType = GRAPHQL_CACHE_UPDATED,
		queryParams = []
	} = options;

	const containerId = coeffects.action.meta.id;
	const registryId = createRegistryId(containerId, subscriptionId);

	const {action, dispatch, host} = coeffects;
	const {payload = {}} = action;
	const variables = pick(payload, variableList);
	const templateVars = pick(payload, templateVarList);
	const headers = pick(payload, headerList);
	const params = pick(payload, queryParams);

	const parentSpan = action.meta.span;
	const gqlSpan =
		parentSpan &&
		trace(SPAN_FOR_GRAPHQL, {
			...getSpanOptions(host),
			kind: SPAN_KIND_CLIENT,
			traceId: parentSpan.traceId,
			...(isValidTraceId(parentSpan.traceId) && {parentSpan})
		});

	const actionMetaData = {
		...action.meta,
		...(gqlSpan && isValidTraceId(gqlSpan.traceId) && {span: gqlSpan}),
		options: {
			query,
			variables,
			templateVars,
			headers
		}
	};
	addPendingNetworkActionForHost(action, host);
	try {
		const queryContext = new QueryContext({
			initialPayloadActionType: streaming?.initialPayloadActionType,
			additionalDataActionType: streaming?.additionalDataActionType,
			successActionType,
			dispatch,
			actionMetaData,
			span: gqlSpan && isValidTraceId(gqlSpan.traceId) ? gqlSpan : null
		});
		const operationName = getOperationName(host.tagName);
		const domainContext = resolveDomainContext(host);

		if (domainContext) actionMetaData.domainContext = domainContext;

		dispatch(startActionType, {}, actionMetaData);
		const interactionId = getInteractionId(action.meta);

		const result = yield sendRequest(
			operationName,
			variables,
			templateVars,
			domainContext,
			headers,
			{
				host,
				interactionId,
				actionType: action.type,
				cacheable,
				__unstableDisableBatching: payload.__unstableDisableBatching,
				__unstableQueryHash,
				query,
				params
			},
			queryContext
		);
		const {
			headers: responseHeaders,
			isCachedResult = false,
			refetchPromise = null,
			...successPayload
		} = result;

		queryContext.successPayloadReceived(successPayload, responseHeaders);
		if (isCachedResult && refetchPromise) {
			defer(() => {
				refetchPromise().then(refetchResult => {
					// compare cachedResult and refetchResult, refetch result will not be streamed
					// so can use refetchResult directly for comparison
					if (!isEqual(queryContext.finalPayload.data, refetchResult[0].data)) {
						dispatch(cacheUpdateActionType, refetchResult[0], {
							...actionMetaData,
							span: gqlSpan,
							responseHeaders
						});
					}
				});
			}, cacheDelay);
		}

		// isSubscription() searches for path 'definitions[0].operation' in the original document
		// for better performance, shortcut subscription detection without using createGraphQLDocument
		if (query?.trimStart().startsWith('subscription')) {
			const {data} = result;
			const ambClient = getAMBClient();
			handleGraphQLSubscription(
				action,
				options,
				data,
				actionMetaData,
				dispatch,
				registryId,
				ambClient,
				ambChannelRegistry
			);
		}
	} catch (error) {
		const {response = {}, result} = error;
		const {status, statusText} = response;
		const responseHeaders = response?.headers;

		dispatch(
			errorActionType,
			error,
			{
				...actionMetaData,
				...(gqlSpan && isValidTraceId(gqlSpan.traceId) && {span: gqlSpan}),
				responseHeaders
			},
			true
		);

		// Standard action dispatched on HTTP errors from the server
		const {
			type,
			payload,
			meta,
			error: httpError
		} = createHttpErrorAction(status, statusText, '', result, actionMetaData);
		if (gqlSpan && isValidTraceId(gqlSpan.traceId)) meta.span = gqlSpan;
		dispatch(type, payload, meta, httpError);
	} finally {
		removePendingNetworkActionForHost(action, host);
		if (gqlSpan) gqlSpan.end();
	}
}

/**
 * Effect that makes a GraphQL request and dispatches actions at relevant parts of the life cycle:
 *  - Before the request is sent
 *  - After the request has returned (success or error)
 *
 * The Success and Action Types that are returned also contain a `meta` property with helpful information
 * useful for debugging purposes including the response headers.
 *
 * @function createGraphQLEffect
 * @example
 * import {createGraphQLEffect} from '@servicenow/ui-effect-graphql';
 *
 * const incidentQuery = `query ($sys_id: String!) {
 *                          GlideRecord_Query (sys_id: $sys_id) {
 *                            incident {
 *                              _results {
 *                                sys_id {
 *                                  displayValue
 *                                }
 *                              }
 *                            }
 *                          }
 *                        }`;
 *
 * const fetchIncidentEffect = createGraphQLEffect(incidentQuery, {
 *     variableList: ['sys_id']
 * });
 *
 * // Register with component action handlers:
 * {
 *     actionHandlers:{
 *         'INCIDENT_FETCHED': fetchIncidentEffect
 *     }
 * }
 *
 * @param {String} query - GraphQL query
 * @param {Object} options
 * @param {Array<String>} options.variableList - GraphQL variables to use from action payload
 * @param {Array<String>} options.templateVarList - Template variables to use from action payload
 * @param {Object<String>} options.headerList - GraphQL request headers
 * @param {String} options.startActionType - Action to be dispatched when the GraphQL request has been issued
 * @param {Object} options.streaming - A JSON object to describe how to handle data when a streaming GraphQL response is expected
 * @param {String} options.&lt;streaming&gt;.initialPayloadActionType - The action to be dispatched when the initial part of the GraphQL payload is received
 * @param {String} options.&lt;streaming&gt;.additionalDataActionType - The action to be dispatched when a streaming update for the GraphQL response is received, this action (assuming the GraphQL Endpoint & schema support it) will be called multiple times until the whole payload is received. The updates will be received in the following format https://github.com/graphql/graphql-wg/blob/main/rfcs/DeferStream.md#payload-format
 * @param {String} options.successActionType - Action to be dispatched when the GraphQL request has either successfully completed or failed with an error not related to network issue
 * @param {String} options.cacheUpdateActionType - Action to be dispatched when the GraphQL cache has been updated with a new value
 * @param {String} options.errorActionType - Action to be dispatched only when the GraphQL request has failed due to network errors, not due to errors thrown in Glide
 * @param {String} options.subscriptionStartedActionType - Action to be dispatched when GraphQL subscription starts
 * @param {String} options.subscriptionSuccessActionType - Action to be dispatched when GraphQL subscription succeeds
 * @param {String} options.subscriptionFailedActionType - Action to be dispatched when GraphQL subscription fails
 * @param {String} options.unsubscribeSuccessActionType - Action to be dispatched when unsubscribing from GraphQL subscription
 * @param {String} options.itemEnteredActionType - Action to be dispatched when an item enters the GraphQL subscription query
 * @param {String} options.itemChangedActionType - Action to be dispatched when an item has changed in the GraphQL subscription query
 * @param {String} options.itemExitedActionType - Action to be dispatched when an item exits the GraphQL subscription query
 * @param {Array<String>} options.queryParams - Array of properties in dispatched action to map values to URL query string variables ("api/users?id=abc123)
 * @return {Object} GraphQL Effect Descriptor
 */

export default function createGraphQLEffect(query, options) {
	return {
		effect: gqlEffect,
		args: [query, options, createSubscriptionId(), getAMBClient, ambChannelRegistry]
	};
}
